'use client';

import { createContext, useContext, useEffect, useState } from 'react';

import { type DisplayCurrency, currencySymbol } from '~/lib/currency';

/**
 * Region-resolved display currency. Client components read it via
 * useDisplayCurrency() instead of hardcoding €/$.
 *
 * Pages render statically with the USD default and the provider corrects the
 * region from /api/geo after mount (the old server-side `headers()` read
 * forced every page dynamic). Only the SYMBOL can flash for EU visitors —
 * price numerals are identical across currencies by design (lib/currency.ts).
 */
const CurrencyContext = createContext<DisplayCurrency>('USD');

export function CurrencyProvider({
  currency = 'USD',
  children,
}: React.PropsWithChildren<{ currency?: DisplayCurrency }>) {
  const [value, setValue] = useState<DisplayCurrency>(currency);

  // Justified useEffect: post-hydration sync with a per-visitor endpoint —
  // this data must not participate in the (shared, static) server render.
  // Deferred to idle: only the currency SYMBOL differs, so this fetch has no
  // business competing with hydration or the LCP window.
  useEffect(() => {
    let active = true;

    const lookup = () => {
      fetch('/api/geo')
        .then((res) => (res.ok ? res.json() : null))
        .then((data: { currency?: string } | null) => {
          if (
            active &&
            (data?.currency === 'EUR' || data?.currency === 'USD')
          ) {
            setValue(data.currency);
          }
        })
        .catch(() => {
          // Geo lookup is cosmetic; USD default is always safe.
        });
    };

    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(lookup, { timeout: 5000 });
      return () => {
        active = false;
        window.cancelIdleCallback(id);
      };
    }

    const t = setTimeout(lookup, 2000);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, []);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useDisplayCurrency() {
  const currency = useContext(CurrencyContext);
  return { currency, symbol: currencySymbol(currency) };
}
